/**
 * KẺ ĐỊCH — 5 phe đối xứng với 5 class defend + boss archetype × affix.
 *
 * "Mỗi wave có boss riêng": ta dùng 5 ARCHETYPE BOSS × 5 AFFIX = 25 biến thể,
 * wave 10 có boss độc quyền. Nhờ vậy mỗi wave vẫn thấy boss mới mà không cần
 * vẽ 10 con boss riêng cho mỗi màn.
 */
import type { Stats } from '../sim/types';

export interface EnemyDef {
  id: string;
  name: string;
  faction: string;
  /** mô tả ngắn hiện trong game */
  blurb: string;
  color: number;
  accent: number;
  base: Stats;
  radius: number;
  /** hành vi */
  behavior: 'melee' | 'ranged' | 'spawner';
  /** khoảng cách mong muốn khi đánh ranged */
  keepDistance?: number;
  /** tần suất đẻ quân (spawner) */
  spawnEvery?: number;
  spawnKind?: string;
  /** loại đạn */
  projectile?: 'bullet' | 'thrown';
  goldMult?: number;
}

export interface BossAffix {
  id: string;
  name: string;
  blurb: string;
  hpMult: number;
  atkMult: number;
  defMult: number;
  /** hiệu ứng đặc biệt */
  special?: 'summon' | 'reflect' | 'regen' | 'frenzy' | 'armored' | 'none';
}

export const ENEMIES: Record<string, EnemyDef> = {
  thug: {
    id: 'thug',
    name: 'Đầu Gấu Xóm',
    faction: 'Đầu gấu',
    blurb: 'Cận chiến, đông, chuyên đập Nhà',
    color: 0x9a3412,
    accent: 0xfdba74,
    radius: 17,
    behavior: 'melee',
    base: {
      hp: 260, atk: 46, def: 18, mana: 0, stamina: 0,
      atkSpeed: 0.9, range: 46, moveSpeed: 92, dodge: 0.03, crit: 0.05,
      manaRegen: 0, staminaRegen: 0,
    },
  },
  runner: {
    id: 'runner',
    name: 'Thân Pháp Quái',
    faction: 'Thân pháp',
    blurb: 'Chạy nhanh, né cao, hay lách qua hàng thủ',
    color: 0x0369a1,
    accent: 0x7dd3fc,
    radius: 15,
    behavior: 'melee',
    base: {
      hp: 170, atk: 34, def: 10, mana: 0, stamina: 0,
      atkSpeed: 1.9, range: 44, moveSpeed: 178, dodge: 0.28, crit: 0.1,
      manaRegen: 0, staminaRegen: 0,
    },
  },
  brawler: {
    id: 'brawler',
    name: 'Đầu Gấu Chợ',
    faction: 'Chợ búa',
    blurb: 'Ném đồ từ xa vào cả hàng thủ',
    color: 0x15803d,
    accent: 0x86efac,
    radius: 17,
    behavior: 'ranged',
    keepDistance: 165,
    projectile: 'thrown',
    base: {
      hp: 210, atk: 40, def: 14, mana: 0, stamina: 0,
      atkSpeed: 0.8, range: 190, moveSpeed: 88, dodge: 0.08, crit: 0.06,
      manaRegen: 0, staminaRegen: 0,
    },
  },
  gunner: {
    id: 'gunner',
    name: 'Lính Súng',
    faction: 'Quân đội',
    blurb: 'Bắn xa, sát thương cao, máu mỏng',
    color: 0x4338ca,
    accent: 0xc7d2fe,
    radius: 16,
    behavior: 'ranged',
    keepDistance: 215,
    projectile: 'bullet',
    base: {
      hp: 190, atk: 58, def: 12, mana: 0, stamina: 0,
      atkSpeed: 1.0, range: 245, moveSpeed: 82, dodge: 0.05, crit: 0.12,
      manaRegen: 0, staminaRegen: 0,
    },
  },
  spawner: {
    id: 'spawner',
    name: 'Mụ Buôn Nước Bọt',
    faction: 'Mẹ thiên hạ',
    blurb: 'Đẻ quái liên tục, chỉ số thấp',
    color: 0xbe185d,
    accent: 0xfbcfe8,
    radius: 19,
    behavior: 'spawner',
    spawnEvery: 5,
    spawnKind: 'thug',
    keepDistance: 140,
    base: {
      hp: 340, atk: 26, def: 20, mana: 0, stamina: 0,
      atkSpeed: 0.6, range: 110, moveSpeed: 74, dodge: 0.04, crit: 0.04,
      manaRegen: 0, staminaRegen: 0,
    },
  },
  /** đệ tử của "Mẹ thiên hạ" phe ta */
  brat: {
    id: 'brat',
    name: 'Đứa Con',
    faction: 'Mẹ thiên hạ',
    blurb: 'Chỉ số thấp nhưng đông',
    color: 0xf472b6,
    accent: 0xfce7f3,
    radius: 12,
    behavior: 'melee',
    base: {
      hp: 190, atk: 30, def: 12, mana: 0, stamina: 0,
      atkSpeed: 1.2, range: 42, moveSpeed: 150, dodge: 0.08, crit: 0.05,
      manaRegen: 0, staminaRegen: 0,
    },
  },
  eliteBrat: {
    id: 'eliteBrat',
    name: 'Con Cưng',
    faction: 'Mẹ thiên hạ',
    blurb: 'Con tinh nhuệ, mạnh gấp đôi',
    color: 0xdb2777,
    accent: 0xfbcfe8,
    radius: 14,
    behavior: 'melee',
    base: {
      hp: 620, atk: 68, def: 30, mana: 0, stamina: 0,
      atkSpeed: 1.5, range: 46, moveSpeed: 165, dodge: 0.14, crit: 0.12,
      manaRegen: 0, staminaRegen: 0,
    },
  },
};

/** 5 AFFIX boss ⇒ 5 archetype × 5 affix = 25 biến thể boss khác nhau. */
export const BOSS_AFFIXES: BossAffix[] = [
  { id: 'armored', name: 'Giáp Sắt', blurb: '+90% giáp, máu dày', hpMult: 8, atkMult: 1.9, defMult: 1.9, special: 'armored' },
  { id: 'frenzy', name: 'Cuồng Nộ', blurb: 'Đánh rất nhanh khi máu thấp', hpMult: 7, atkMult: 2.1, defMult: 1.2, special: 'frenzy' },
  { id: 'summoner', name: 'Triệu Hồi', blurb: 'Gọi quái liên tục', hpMult: 7.5, atkMult: 1.8, defMult: 1.3, special: 'summon' },
  { id: 'reflect', name: 'Phản Đòn', blurb: 'Phản lại 25% sát thương nhận vào', hpMult: 7, atkMult: 1.9, defMult: 1.4, special: 'reflect' },
  { id: 'regen', name: 'Hồi Sinh', blurb: 'Hồi 2% máu mỗi giây', hpMult: 8.5, atkMult: 1.7, defMult: 1.3, special: 'regen' },
];

/** 5 archetype boss nền. */
export const BOSS_ARCHETYPES: string[] = ['thug', 'runner', 'brawler', 'gunner', 'spawner'];

/** Boss độc quyền wave 10 của màn 1 — to, xấu, nhiều chiêu. */
export const STAGE_BOSS: EnemyDef = {
  id: 'stageBoss',
  name: 'Tàu Hỏa Vương',
  faction: 'Trùm màn',
  blurb: 'Trùm cuối màn: vừa trâu vừa triệu hồi vừa phản đòn',
  color: 0x7f1d1d,
  accent: 0xfca5a5,
  radius: 34,
  behavior: 'melee',
  base: {
    hp: 5200, atk: 165, def: 95, mana: 0, stamina: 0,
    atkSpeed: 0.85, range: 78, moveSpeed: 68, dodge: 0.06, crit: 0.15,
    manaRegen: 0, staminaRegen: 0,
  },
};
